import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { MessageSquarePlus, Upload, X, FileText, Image, Bug, Lightbulb, MessageCircle, HelpCircle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { usePlatformFeedback, FeedbackType, FeedbackStatus } from '@/hooks/usePlatformFeedback';
import { toast } from 'sonner';

const feedbackSchema = z.object({
  feedback_type: z.enum(['bug_report', 'feature_request', 'general_feedback', 'other']),
  subject: z.string().min(5, 'Subject must be at least 5 characters').max(200, 'Subject must be 200 characters or less'),
  message: z.string().min(20, 'Message must be at least 20 characters').max(5000, 'Message must be 5000 characters or less'),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

const feedbackTypeConfig: Record<FeedbackType, { label: string; icon: React.ElementType; color: string }> = {
  bug_report: { label: 'Bug Report', icon: Bug, color: 'bg-destructive/10 text-destructive' },
  feature_request: { label: 'Feature Request', icon: Lightbulb, color: 'bg-warning/10 text-warning' },
  general_feedback: { label: 'General Feedback', icon: MessageCircle, color: 'bg-primary/10 text-primary' },
  other: { label: 'Other', icon: HelpCircle, color: 'bg-muted text-muted-foreground' },
};

const statusConfig: Record<FeedbackStatus, { label: string; color: string }> = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  wont_fix: { label: "Won't Fix", color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
};

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function FeedbackPage() {
  const { myFeedback, loading, submitting, submitFeedback } = usePlatformFeedback();
  const [files, setFiles] = useState<File[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      feedback_type: 'general_feedback',
      subject: '',
      message: '',
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    const newFiles = [...files, ...validFiles].slice(0, MAX_FILES);
    if (files.length + validFiles.length > MAX_FILES) {
      toast.warning(`Maximum ${MAX_FILES} files allowed`);
    }
    setFiles(newFiles);
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: MAX_FILES - files.length,
    disabled: files.length >= MAX_FILES,
  });

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FeedbackFormData) => {
    const success = await submitFeedback({
      feedback_type: data.feedback_type,
      subject: data.subject,
      message: data.message,
      files: files.length > 0 ? files : undefined,
    });

    if (success) {
      form.reset();
      setFiles([]);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />;
    }
    return <FileText className="h-4 w-4" />;
  };

  return (
    <Layout>
      <div className="container max-w-3xl py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquarePlus className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Submit Feedback</h1>
          </div>
          <p className="text-muted-foreground">
            Help us improve by sharing your feedback, reporting bugs, or suggesting new features.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>New Feedback</CardTitle>
            <CardDescription>
              Fill out the form below to submit your feedback to our team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="feedback_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Feedback Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(feedbackTypeConfig).map(([value, config]) => {
                            const Icon = config.icon;
                            return (
                              <SelectItem key={value} value={value}>
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  {config.label}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Brief summary of your feedback" 
                          {...field} 
                          maxLength={200}
                        />
                      </FormControl>
                      <div className="text-xs text-muted-foreground text-right">
                        {field.value?.length || 0}/200
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Please provide as much detail as possible..." 
                          className="min-h-[150px]"
                          {...field} 
                          maxLength={5000}
                        />
                      </FormControl>
                      <div className="text-xs text-muted-foreground text-right">
                        {field.value?.length || 0}/5000
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <Label>Attachments (Optional)</Label>
                  <div
                    {...getRootProps()}
                    className={`
                      border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                      transition-colors
                      ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                      ${files.length >= MAX_FILES ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    {isDragActive ? (
                      <p className="text-sm">Drop files here...</p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Drag & drop files here, or click to select
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG, JPEG, or PDF (max 10MB each, up to {MAX_FILES} files)
                        </p>
                      </>
                    )}
                  </div>

                  {files.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-muted rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {getFileIcon(file)}
                            <span className="text-sm truncate">{file.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Feedback'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {myFeedback.length > 0 && (
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Your Previous Submissions</CardTitle>
                      <CardDescription>
                        {myFeedback.length} submission{myFeedback.length !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                    {historyOpen ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      myFeedback.map((item) => {
                        const typeConfig = feedbackTypeConfig[item.feedback_type];
                        const TypeIcon = typeConfig.icon;
                        const status = statusConfig[item.status];
                        
                        return (
                          <div
                            key={item.id}
                            className="border rounded-lg p-4 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-center gap-2 min-w-0">
                                <Badge variant="secondary" className={typeConfig.color}>
                                  <TypeIcon className="h-3 w-3 mr-1" />
                                  {typeConfig.label}
                                </Badge>
                                <Badge variant="outline" className={status.color}>
                                  {status.label}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {format(new Date(item.created_at), 'MMM d, yyyy')}
                              </span>
                            </div>
                            <h4 className="font-medium">{item.subject}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {item.message}
                            </p>
                            {item.attachment_urls && item.attachment_urls.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <FileText className="h-3 w-3" />
                                {item.attachment_urls.length} attachment{item.attachment_urls.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
      </div>
    </Layout>
  );
}
